import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";
import {Product} from "../../../models/product";
import {getPageDefaults, getPageNumberAndSize, Page} from "../../../models/paginator";
import {environment} from "../../../environments/environment";
import {getSearchedEntity} from "../../../services/util/util";
import {HttpClient, HttpParams} from "@angular/common/http";
import {Search} from "../../../models/filters";
import {Discount} from "../../../models/discount";


@Injectable()
export class ShopService {

    private productsPageSubject$: BehaviorSubject<Page<Product>> = new BehaviorSubject<Page<Product>>(getPageDefaults());

    constructor(
        private readonly httpClient: HttpClient) {
    }

    get productsPage$(): Observable<Page<Product>> {
        return this.productsPageSubject$.asObservable();
    }

    getAllProducts(pageNumber: number, pageSize: number): void {
        const url: string = environment.domain.backend + environment.api.products + "/paged";

        const params = new HttpParams()
            .set('page', pageNumber.toString())
            .set('size', pageSize.toString())
            .set('sort', "created,asc");

        this.httpClient.get<Page<Product>>(url, {params}).subscribe((page) => {
            this.productsPageSubject$.next(page);
        });
    }

    getProductById(id: number): Observable<Product> {
        const url: string =
            environment.domain.backend +
            environment.api.products +
            "/" + id;

        return this.httpClient.get<Product>(url);
    }

    getProductsByIds(ids: number[]): Observable<Product[]> {
        const url: string =
            environment.domain.backend +
            environment.api.products +
            "/ids";

        return this.httpClient.post<Product[]>(url, ids);
    }

    getProductsByCategory(category: string): Observable<Product[]> {
        const url: string =
            environment.domain.backend +
            environment.api.products +
            "/category?category=" + category.toUpperCase();

        return this.httpClient.get<Product[]>(url);
    }

    getTopSellingProducts(limit: number): Observable<Product[]> {
        const url: string =
            environment.domain.backend +
            environment.api.products +
            "/top-selling?limit=" + limit;

        return this.httpClient.get<Product[]>(url);
    }

    getSearchedProducts(search: Search, pageNumber?: number, pageSize?: number): void {
        const url: string =
            environment.domain.backend +
            environment.api.products +
            "/search" +
            getPageNumberAndSize(pageNumber, pageSize);

        getSearchedEntity<Product>(search, this.productsPageSubject$, this.httpClient, url);
    }

    /** What other buyers ordered with this product (filled up with related products and best sellers). */
    getBoughtTogether(productId: number, limit: number): Observable<Product[]> {
        return this.httpClient.get<Product[]>(
            `${environment.domain.backend}${environment.api.products}/${productId}/bought-together?limit=${limit}`);
    }

    getDiscountedProducts(): Observable<Product[]> {
        const url: string =
            environment.domain.backend +
            environment.api.products +
            "/on-discount";

        return this.httpClient.get<Product[]>(url);
    }

    addProduct(product: Partial<Product>): Observable<Product> {
        const url: string = environment.domain.backend + environment.api.products;

        return this.httpClient.post<Product>(url, product);
    }

    updateProduct(product: Partial<Product>): Observable<Product> {
        const url: string = environment.domain.backend + environment.api.products + "/" + product.id;

        return this.httpClient.put<Product>(url, product);
    }

    deleteProduct(productId: number): Observable<void> {
        const url: string = environment.domain.backend + environment.api.products + "/" + productId;

        return this.httpClient.delete<void>(url);
    }

    getAllDiscounts(): Observable<Discount[]> {
        const url: string = environment.domain.backend + environment.api.products + environment.api.discounts;

        return this.httpClient.get<Discount[]>(url);
    }

    applyDiscount(productId: number, discountId: number): Observable<Product> {
        const url: string = environment.domain.backend + environment.api.products + "/" + productId + "/apply-discount?discountId=" + discountId;

        return this.httpClient.post<Product>(url, null);
    }

    removeProductDiscount(productId: number): Observable<Product> {
        const url: string = environment.domain.backend + environment.api.products + "/" + productId + "/remove-discount";

        return this.httpClient.post<Product>(url, null);
    }
}